import React, { useState, useEffect } from "react";
import CanvasJSReact from "@canvasjs/react-charts";
import { VscOpenPreview } from "react-icons/vsc";
import { FaEdit, FaExpandAlt } from "react-icons/fa";
import DrawingOverlay from "./DrawingOverlay";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function Reenrolment({ expanded, setExpanded, selectedQuarter, selectedYear }) {
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [attritionData, setAttritionData] = useState([]);
  const [prevAttritionData, setPrevAttritionData] = useState([]);
  const [studentEvalData, setStudentEvalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAttritionRate = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getAttritionRate?qtr=${selectedQuarter}&year=${selectedYear}`
        );

        setAttritionData(res.data?.current ?? []);
        setPrevAttritionData(res.data?.previous ?? []);
      } catch (error) {
        console.error("Error fetching attrition data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchStudentEvaluation = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getStudentEvalByCourses?qtr=${selectedQuarter}&year=${selectedYear}`
        );
        if (res.data?.success) {
          setStudentEvalData(res.data.current ?? []);
        } else {
          console.error(
            "Failed to fetch student evaluation data:",
            res.data.message
          );
        }
      } catch (error) {
        console.error("Error fetching student evaluation data:", error);
      }
    };

    fetchAttritionRate();
    fetchStudentEvaluation();
  }, [selectedQuarter, selectedYear]);

  const prepareChartData = () => {
    const currentDataPoints = Array.isArray(attritionData)
      ? attritionData
          .filter((item) => parseFloat(item.totalWithdrawn ?? 0) !== 0)
          .map((item) => ({
            label: item.course_code ?? "Unknown",
            y: parseFloat(item.totalWithdrawn ?? 0),
          }))
      : [];

    const prevDataPoints = Array.isArray(prevAttritionData)
      ? prevAttritionData
          .filter((item) => parseFloat(item.totalWithdrawn ?? 0) !== 0)
          .map((item) => ({
            label: item.course_code ?? "Unknown",
            y: parseFloat(item.totalWithdrawn ?? 0),
          }))
      : [];

    return { currentDataPoints, prevDataPoints };
  };

  const prepareStudentEvalChartData = () => {
    if (!Array.isArray(studentEvalData) || studentEvalData.length === 0) {
      return [];
    }
    return studentEvalData.map((item) => ({
      label: item.course ?? "Unknown",
      y: parseFloat(item.avgSec ?? 0),
    }));
  };

  let currentDataPoints = [];
  let prevDataPoints = [];

  if (!loading && attritionData && prevAttritionData) {
    ({ currentDataPoints, prevDataPoints } = prepareChartData());
  }

  const headerStyle = {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleViewTable = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/table", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
        currentData: attritionData,
        previousData: prevAttritionData,
        title: "Attrition Rate",
      },
    });
  };

  const handleStudentTable = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/detailstudentevaluation", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
      },
    });
  };

  const chartBoxStyle = {
    backgroundColor: "#2f2f2f",
    borderRadius: 10,
    padding: 20,
    minWidth: 300,
    height: "620px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  const iconStyle = {
    position: "absolute",
    top: 15,
    right: 15,
    color: "#bbb",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#373636",
        margin: 20,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h5 style={{ color: "#fff", margin: 0 }}>
          Attrition Rate & Student Evaluation
        </h5>

        <div style={{ display: "flex", gap: 12 }}>
          <FaEdit
            size={20}
            style={{ color: "#fff", cursor: "pointer" }}
            onClick={() => setIsDrawingMode(!isDrawingMode)}
          />
          <FaExpandAlt
            size={20}
            onClick={handleToggleExpand}
            style={{ color: "#fff", cursor: "pointer" }}
            title={expanded ? "Collapse" : "Expand"}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        {/* Column 1: Bar Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
              <div title="View Attrition rate">
                <VscOpenPreview size={18} onClick={handleViewTable} />
              </div>
            </div>
            <div style={headerStyle} className="mb-4">
              ATTRITION RATE
            </div>
            {loading ? (
              <div style={{ color: "#fff", textAlign: "center" }}>
                Loading...
              </div>
            ) : (
              <CanvasJSChart
                options={{
                  height: 500,
                  animationEnabled: true,
                  backgroundColor: "#2f2f2f",
                  axisX: {
                    title: "Course",
                    labelFontColor: "#ccc",
                    titleFontColor: "#ccc",
                    interval: 1,
                    labelFontSize: 10,
                  },
                  axisY: {
                    title: "Total Withdrawn",
                    includeZero: true,
                    labelFontColor: "#ccc",
                    titleFontColor: "#ccc",
                    gridColor: "#444",
                    interval: 1,
                  },
                  toolTip: {
                    shared: true,
                  },
                  legend: {
                    cursor: "pointer",
                    fontColor: "#fff",
                  },
                  data: [
                    {
                      type: "bar",
                      name: `Q${
                        selectedQuarter === 1 ? 4 : selectedQuarter - 1
                      } ${
                        selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                      }`,
                      showInLegend: true,
                      color: "#F2D092",
                      dataPoints: prevDataPoints,
                    },
                    {
                      type: "bar",
                      name: `Q${selectedQuarter || 1} ${selectedYear}`,
                      showInLegend: true,
                      color: "#FFA500",
                      dataPoints: currentDataPoints,
                    },
                  ],
                }}
              />
            )}
          </div>
        </div>

        {/* Column 2: Bar Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
              <div title="View Student Evaluation">
                <VscOpenPreview size={18} onClick={handleStudentTable} />
              </div>
            </div>

            <div style={headerStyle} className="mb-4">
              STUDENT EVALUATION
            </div>
            <CanvasJSChart
              options={{
                height: 500,
                animationEnabled: true,
                backgroundColor: "#2f2f2f",
                axisX: {
                  title: "Course",
                  labelFontColor: "#ccc",
                  titleFontColor: "#ccc",
                  interval: 1,
                  labelFontSize: 12,
                  titleFontSize: 14,
                },
                axisY: {
                  title: "Avg Evaluation Score",
                  labelFontColor: "#ccc",
                  titleFontColor: "#ccc",
                  gridColor: "#444",
                  labelFontSize: 12,
                  titleFontSize: 14,
                },
                toolTip: {
                  shared: true,
                },
                legend: {
                  cursor: "pointer",
                  fontColor: "#fff",
                  fontSize: 14,
                  horizontalAlign: "center",
                  verticalAlign: "bottom",
                },
                data: [
                  {
                    type: "area",
                    name: "Student Evaluation",
                    showInLegend: true,
                    color: "#2F80ED",
                    markerColor: "#2F80ED",
                    indexLabel: "{y}",
                    indexLabelFontColor: "#fff",
                    indexLabelFontSize: 12,
                    dataPoints: prepareStudentEvalChartData(),
                  },
                ],
              }}
            />
          </div>
        </div>
      </div>
      {isDrawingMode && (
        <DrawingOverlay onClose={() => setIsDrawingMode(false)} />
      )}
    </div>
  );
}

export default Reenrolment;
